import debug, {ERROR} from './debug'

export type EventName = string
export type EventListener = (eventName: EventName, ...args: any[]) => void
export type EventListeners = Set<EventListener>
export type EventListenerRemover = () => boolean

export class Emitter {
  private eventToListenersMap: Map<EventName, EventListeners>
  private destinations: Set<Emitter>

  constructor () {
    this.eventToListenersMap = new Map()
    this.destinations = new Set()
  }

  public emit (eventName: EventName, ...args: any[]): void {
    const listeners = this.eventToListenersMap.get(eventName)

    if (listeners) {
      listeners.forEach(listener => {
        listener(eventName, ...args)
      })
    }

    this.destinations.forEach(destination => {
      destination.emit(eventName, ...args)
    })
  }

  public off (eventName: EventName, listener: EventListener): boolean {
    return this.eventToListenersMap.has(eventName) && this.eventToListenersMap.get(eventName)!.delete(listener)
  }

  public on (eventName: EventName, listener: EventListener): EventListenerRemover {
    let listeners = this.eventToListenersMap.get(eventName)

    if (listeners) {
      listeners.add(listener)
    } else {
      listeners = new Set([listener])
      this.eventToListenersMap.set(eventName, listeners)
    }

    return this.off.bind(this, eventName, listener)
  }

  public once (eventName: EventName, listener: EventListener): EventListenerRemover {
    const off = this.on(eventName, (eventName, ...args) => {
      off()
      listener(eventName, ...args)
    })

    return off
  }

  public pipeEvents (emitter: Emitter): void {
    this.destinations.add(emitter)
  }

  public future (eventName: EventName): Promise<any[]> {
    return new Promise(resolve => {
      this.once(eventName, (...args: any[]) => {
        resolve(args)
      })
    })
  }
}

export interface EventEmitter {
  emit (name: EventName, ...args: any[]): void,
  off (name: EventName, listener: EventListener): boolean,
  on (name: EventName, listener: EventListener): EventListenerRemover,
  once (name: EventName, listener: EventListener): EventListenerRemover,
  pipeEvents (emitter: Emitter): void
}

export class MultiWindowEmitter extends Emitter {
  public emit (name: EventName, ...args: any[]): void {
    super.emit(name, ...args)
    const eventName = this.createStorageName(name)
    localStorage.setItem(eventName, JSON.stringify(args))
    localStorage.removeItem(eventName)
  }

  public on (name: EventName, listener: EventListener): EventListenerRemover {
    const off = super.on(name, listener)

    const handler = ({key, newValue}: StorageEvent) => {
      if (!newValue || key !== this.createStorageName(name)) {
        return
      }

      try {
        const args = JSON.parse(newValue)
        listener(name, args)
      } catch (error) {
        debug(error, ERROR)
      }
    }

    window.addEventListener('storage', handler)

    return () => {
      window.removeEventListener('storage', handler)
      return off()
    }
  }

  private createStorageName (name: EventName): string {
    return `mol.ads.cmp.event.${name}`
  }
}
